import { getUserProfile, updateUserName } from '@db/services/profile.service';
import { memo, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import { Button } from '../Button';

export const UserNameSettings = memo(function UserNameSettings() {
  const { user, setProfileUserName } = useAuthStore();
  const [userName, setUserName] = useState('');
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase || !user?.id) {
      setIsLoadingProfile(false);
      return;
    }
    let cancelled = false;
    setIsLoadingProfile(true);
    setError(null);
    getUserProfile(supabase, user.id)
      .then((profile) => {
        if (!cancelled && profile) setUserName(profile.user_name);
        else if (!cancelled)
          setUserName(user.user_metadata?.full_name || user.email || '');
      })
      .catch((e) => {
        if (!cancelled)
          setError(
            e instanceof Error ? e.message : 'プロフィールの取得に失敗しました',
          );
      })
      .finally(() => {
        if (!cancelled) setIsLoadingProfile(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.user_metadata?.full_name, user?.email]);

  const handleUpdateUserName = async () => {
    if (!user) return;
    const trimmed = userName.trim();
    setError(null);
    if (trimmed.length < 1) {
      setError('ユーザー名は1文字以上入力してください');
      return;
    }
    if (!supabase || !user.id) {
      setError('ログイン状態を確認できません');
      return;
    }
    setIsSaving(true);
    try {
      await updateUserName(supabase, user.id, trimmed);
      setUserName(trimmed);
      setProfileUserName(trimmed);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'ユーザー名の更新に失敗しました',
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="grid gap-8">
      <header className="flex items-center gap-[1em] justify-between text-xl">
        <h3 className="font-bold text-base text-gray-800 shrink-0">
          ユーザー名
        </h3>
        <p className="text-gray-600 text-xs md:text-base">
          ランキング表示名を変更
        </p>
      </header>
      <div className="grid gap-8">
        <input
          type="text"
          value={userName || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setUserName(e.target.value)
          }
          placeholder="ユーザー名"
          className="w-full px-[1em] py-[0.8em] text-gray-800 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
          disabled={isLoadingProfile || isSaving}
        />
        {error && (
          <p className="text-red-600 text-xs" role="alert">
            {error}
          </p>
        )}
        <Button
          onClick={handleUpdateUserName}
          disabled={isSaving}
          className="text-base"
          invert
        >
          {isSaving ? '保存中…' : '保存'}
        </Button>
      </div>
    </section>
  );
});
